import { PoseLandmarkerResult, NormalizedLandmark } from '@mediapipe/tasks-vision';
import { PoseData, PoseLandmark } from '../../types';
import { PoseSmoother } from './PoseSmoother';

/**
 * PoseProcessor converts MediaPipe landmark format to PoseData interface
 * and applies smoothing to reduce jitter
 * Validates Requirements 3.1.3, 3.1.6, 3.1.7, 1.1
 */
export class PoseProcessor {
  private smoothers: Map<number, PoseSmoother>;

  constructor() {
    this.smoothers = new Map();
  }
  // MediaPipe Pose landmark indices
  private static readonly LANDMARK_INDICES = {
    NOSE: 0,
    LEFT_EYE: 2,
    RIGHT_EYE: 5,
    LEFT_EAR: 7,
    RIGHT_EAR: 8,
    LEFT_SHOULDER: 11,
    RIGHT_SHOULDER: 12,
    LEFT_ELBOW: 13,
    RIGHT_ELBOW: 14,
    LEFT_WRIST: 15,
    RIGHT_WRIST: 16,
    LEFT_HIP: 23,
    RIGHT_HIP: 24,
    LEFT_KNEE: 25,
    RIGHT_KNEE: 26,
    LEFT_ANKLE: 27,
    RIGHT_ANKLE: 28
  };

  /**
   * Convert MediaPipe result to array of PoseData with smoothing applied
   * @param result MediaPipe detection result
   * @returns Array of smoothed PoseData (empty array if no poses detected)
   */
  process(result: PoseLandmarkerResult | null): PoseData[] {
    if (!result || !result.landmarks || result.landmarks.length === 0) {
      // No poses detected - reset all smoothers
      this.smoothers.clear();
      return [];
    }

    const processedPoses: PoseData[] = [];

    // Process each detected pose independently
    for (let i = 0; i < result.landmarks.length; i++) {
      const landmarks = result.landmarks[i];
      
      // Ensure we have enough landmarks
      if (!landmarks || landmarks.length < 33) {
        continue; // Skip invalid pose data
      }
      
      // Convert raw landmarks to PoseData
      const rawPoseData: PoseData = {
        landmarks: {
          nose: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.NOSE]!),
          leftEye: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_EYE]!),
          rightEye: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_EYE]!),
          leftEar: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_EAR]!),
          rightEar: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_EAR]!),
          leftShoulder: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_SHOULDER]!),
          rightShoulder: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_SHOULDER]!),
          leftElbow: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_ELBOW]!),
          rightElbow: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_ELBOW]!),
          leftWrist: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_WRIST]!),
          rightWrist: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_WRIST]!),
          leftHip: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_HIP]!),
          rightHip: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_HIP]!),
          leftKnee: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_KNEE]!),
          rightKnee: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_KNEE]!),
          leftAnkle: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.LEFT_ANKLE]!),
          rightAnkle: this.convertLandmark(landmarks[PoseProcessor.LANDMARK_INDICES.RIGHT_ANKLE]!)
        },
        timestamp: performance.now(),
        detected: true
      };

      // Get or create smoother for this pose index
      if (!this.smoothers.has(i)) {
        this.smoothers.set(i, new PoseSmoother());
      }
      const smoother = this.smoothers.get(i)!;

      // Apply smoothing to reduce jitter
      const smoothedPoseData = smoother.smooth(rawPoseData);
      processedPoses.push(smoothedPoseData);
    }

    // Clean up smoothers for pose indices that are no longer present
    const currentIndices = new Set(Array.from({ length: result.landmarks.length }, (_, i) => i));
    for (const [index] of this.smoothers) {
      if (!currentIndices.has(index)) {
        this.smoothers.delete(index);
      }
    }

    return processedPoses;
  }

  /**
   * Convert MediaPipe NormalizedLandmark to PoseLandmark
   * Normalizes coordinates to [0, 1] range and handles missing/low-confidence landmarks
   * Applies horizontal mirroring to match user expectations
   */
  private convertLandmark(landmark: NormalizedLandmark): PoseLandmark {
    // MediaPipe already provides normalized coordinates [0, 1]
    // Clamp values to ensure they're within bounds
    const rawX = Math.max(0, Math.min(1, landmark.x));
    const y = Math.max(0, Math.min(1, landmark.y));
    const z = landmark.z || 0;
    
    // Apply horizontal mirroring: x_mirrored = 1 - x_original
    // This makes the pose rendering match the mirrored video display
    const x = 1 - rawX;
    
    // Handle visibility - MediaPipe provides visibility score
    // If visibility < 0.5, consider landmark as not reliable
    const visibility = landmark.visibility !== undefined ? landmark.visibility : 1.0;
    
    return {
      x,
      y,
      z,
      visibility: Math.max(0, Math.min(1, visibility))
    };
  }
}
